import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryParams, StringParam } from 'use-query-params';
import { useFilterCallbacks } from '../utils/api/hooks';
import { createFilterQueryString, IFilterQueryItem } from '../utils/dashboard/filters';
import { MutableRefObject } from 'react';
import { IFilters, IPagination } from '../utils/api/types';
import { ChangeFilters } from '../common/Filters/types';
import { parseFilterQueryString } from '../utils/parseFilterQueryString';

const getIsValidFilterValue = (value: any): boolean => {
  const isInvalid =
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0);

  return !isInvalid;
};

export const useFilterWithUrl = <TResponse = any>(
  _filters: MutableRefObject<IFilters>,
  fetch: () => Promise<TResponse | null>,
  _pagination?: MutableRefObject<IPagination>,
) => {
  const history = useHistory();
  const { setFilters: apiSetFilters, clearFilters: apiClearFilters } = useFilterCallbacks(_filters, fetch, _pagination);
  const [{ filters: urlFilters }] = useQueryParams({ filters: StringParam });

  const updateUrlFromFilters = useCallback(() => {
    const currentFilters = { ..._filters.current };
    const validFilters = Object.entries(currentFilters)
      .filter(([_, value]) => getIsValidFilterValue(value))
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    const filterQueryItems = Object.entries(validFilters)
      .map(([key, value]) => ({ key, value }));

    if (filterQueryItems.length > 0) {
      const queryString = createFilterQueryString(filterQueryItems as IFilterQueryItem[]);
      history.replace(`${history.location.pathname}?filters=${queryString}`);
    } else {
      history.replace(history.location.pathname);
    }
  }, [_filters, history]);

  useEffect(() => {
    if (urlFilters) {
      apiClearFilters(undefined, true);
      const parsedFilters = parseFilterQueryString(urlFilters);
      apiSetFilters(parsedFilters, false);
    } else {
      apiClearFilters(undefined, false);
    }
  }, [urlFilters, apiSetFilters, apiClearFilters]);

  const setFilters = useCallback((filters: ChangeFilters[]) => {
    apiSetFilters(filters, true);
    updateUrlFromFilters();

    return Promise.resolve();
  }, [updateUrlFromFilters, apiSetFilters]);

  const clearFilters = useCallback((keys?: string[]) => {
    apiClearFilters(keys, true);

    if (!keys) {
      history.replace(history.location.pathname);
    } else {
      updateUrlFromFilters();
    }

    return Promise.resolve();
  }, [apiClearFilters, history, updateUrlFromFilters]);

  const replaceFilters = useCallback((newFilters: ChangeFilters[]) => {
    apiClearFilters(undefined, true);
    apiSetFilters(newFilters, true);
    updateUrlFromFilters();

    return Promise.resolve();
  }, [apiClearFilters, apiSetFilters, updateUrlFromFilters]);

  return { setFilters, clearFilters, replaceFilters };
};
